<template>
  <div class="video-wrapper">
    <div class="aspect_video">
      <!-- Preview image + play button -->
      <div v-if="!isPlaying" class="video-poster" @click="playVideo">
        <img
          :src="thumbnail?.original_url ?? thumbnailUrl"
          alt="Video preview"
          loading="lazy"
        />
        <button class="custom-play-btn">
          <Icon name="play-video" />
        </button>
      </div>

      <!-- YouTube iframe -->
      <ClientOnly>
        <div
          v-show="isPlaying"
          ref="youtubePlayerContainer"
          class="youtube-iframe"
        ></div>
      </ClientOnly>
    </div>

    <h2>{{ title }}</h2>
  </div>
</template>

<script lang="ts" setup>
  import type { MediaFragment } from "~/lib/api-types";

  // Extend Window interface to include YouTube API properties
  declare global {
    interface Window {
      onYouTubeIframeAPIReady: () => void;
      YT: any;
    }
  }

  interface Props {
    title: string | null | undefined;
    url: string | null | undefined;
    thumbnail?: MediaFragment | null;
  }

  const props = defineProps<Props>();

  const player = ref<any>(null);
  const youtubePlayerContainer = ref(null);
  const isPlaying = ref(false);

  const videoId =
    props.url?.match(
      /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([a-zA-Z0-9_-]{11})/
    )?.[1] ?? "";

  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;

  onMounted(async () => {
    if (!videoId) return;
    await loadYouTubeAPI();
  });

  async function playVideo() {
    if (!videoId || !youtubePlayerContainer.value) return;

    isPlaying.value = true;

    const YT = await loadYouTubeAPI();
    player.value = new YT.Player(youtubePlayerContainer.value, {
      videoId,
      playerVars: {
        autoplay: 1,
        controls: 0,
        modestbranding: 1,
        rel: 0,
        fs: 0,
        mute: 0,
        playsinline: 1,
        showinfo: 0,
        iv_load_policy: 3,
        enablejsapi: 1,
      },
    });
  }

  async function loadYouTubeAPI(): Promise<typeof window.YT> {
    return new Promise((resolve) => {
      if (window.YT && window.YT.Player) {
        resolve(window.YT);
      } else {
        const tag = document.createElement("script");
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName("script")[0];
        firstScriptTag?.parentNode?.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          resolve(window.YT);
        };
      }
    });
  }
</script>

<style lang="scss">
  .video-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;

    h2 {
      margin-bottom: 0;
    }

    .aspect_video {
      position: relative;
      width: 100%;
      padding-bottom: 56.25%; // 16:9 ratio
      overflow: hidden;

      .video-poster,
      .youtube-iframe {
        position: absolute;
        top: 0;
        left: 0;
        width: 100% !important;
        height: 100% !important;
      }

      .video-poster {
        cursor: pointer;
        z-index: 2;
        display: flex;
        justify-content: center;
        align-items: center;

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .custom-play-btn {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);

          svg {
            width: 40px;
            height: 40px;

            @media (min-width: 768px) {
              width: 64px;
              height: 64px;
            }
          }
        }
      }
    }

    @media (min-width: 1024px) {
      gap: 32px;
    }
  }

  .youtube-iframe iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100% !important;
    height: 100% !important;
    border: 0;
  }
</style>
